import { useEffect, useMemo, useState } from "react";
import { CiFilter } from "react-icons/ci";
import { RiDeleteBinLine } from "react-icons/ri";
import { TiPencil } from "react-icons/ti";
import { FaBoxOpen } from "react-icons/fa";
import type { FiltersTypes } from "../../types/product/product.form";
import { useQueryParams } from "../../hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { deleteProduct, getProducts } from "../../redux/slices/productSlice";
import { getAllCategory } from "../../redux/slices/categorySlice";
import { getAllBrands } from "../../redux/slices/brandSlice";
import { Pagination } from "../common/pagination/Pagination";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import { PageHeader } from "../common/PageHeader";
import { SearchInput } from "../common/SearchInput";
import { EmptyState } from "../common/EmptyState";
import { StatusBadge } from "../common/StatusBadge";
import { DataTable } from "../common/DataTable";
import { ConfirmModal } from "../common/ConfirmModal";

type Payload = {
  onCreate: () => void;
  onUpdate: (id: string) => void;
};

export const ListProduct = ({ onCreate, onUpdate }: Payload) => {
  const dispatch = useAppDispatch();
  const { products, pagination, loading } = useAppSelector(
    (state) => state.product,
  );
  const { allCategory } = useAppSelector((state) => state.category);
  const { allBrands } = useAppSelector((state) => state.brand);

  useEffect(() => {
    dispatch(getAllCategory({}));
    dispatch(getAllBrands({}));
  }, [dispatch]);

  const { getParam, updateParams } = useQueryParams();
  const page = Number(getParam("page", 1));
  const limit = Number(getParam("limit", 10));
  const search = getParam("search", "");
  const isActive = getParam("isActive", "all");
  const categoryId = getParam("categoryId", "");
  const brandId = getParam("brandId", "");

  const [isShowFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState<FiltersTypes>({
    categoryId: categoryId || "",
    brandId: brandId || "",
    isActive: isActive || "all",
  });
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const handleChangeFilters = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    updateParams({
      categoryId: filters.categoryId || undefined,
      brandId: filters.brandId || undefined,
      isActive: filters.isActive === "all" ? undefined : filters.isActive,
      page: 1,
    });
    setShowFilter(false);
  };

  const handleResetFilter = () => {
    setFilters({ categoryId: "", brandId: "", isActive: "all" });
    updateParams({
      categoryId: undefined,
      brandId: undefined,
      isActive: undefined,
      page: 1,
    });
    setShowFilter(false);
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (categoryId) n++;
    if (brandId) n++;
    if (isActive && isActive !== "all") n++;
    return n;
  }, [categoryId, brandId, isActive]);

  const handleChangeLimit = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateParams({ limit: Number(e.target.value), page: 1 });
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;
    try {
      await dispatch(deleteProduct(confirmId)).unwrap();
      toast.success("Xóa sản phẩm thành công");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setConfirmId(null);
    }
  };

  const parserIsActive =
    isActive === "all" ? undefined : isActive === "true" ? true : false;

  useEffect(() => {
    dispatch(
      getProducts({
        page,
        limit,
        search,
        brandId,
        categoryId,
        isActive: parserIsActive,
      }),
    );
  }, [dispatch, page, limit, search, brandId, categoryId, parserIsActive]);

  return (
    <div className="p-5">
      <PageHeader
        title="Quản lý sản phẩm"
        subtitle={
          pagination ? `Tổng ${pagination.total} sản phẩm` : undefined
        }
        actions={
          <button
            type="button"
            onClick={onCreate}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 transition-colors cursor-pointer text-sm text-white rounded-lg font-medium"
          >
            + Thêm sản phẩm
          </button>
        }
      />

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Toolbar */}
        <div className="p-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 border-b border-gray-100">
          <SearchInput
            value={search}
            onChange={(v) => updateParams({ search: v, page: 1 })}
            placeholder="Tìm sản phẩm theo tên..."
            className="w-full lg:w-80"
          />
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowFilter((s) => !s)}
                className="px-3 py-2 border border-gray-200 rounded-xl cursor-pointer flex items-center gap-2 hover:border-blue-500 transition-colors"
              >
                <CiFilter className="text-xl text-gray-700" />
                <span className="text-sm text-gray-700">Bộ lọc</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs font-semibold bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {isShowFilter && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowFilter(false)}
                  />
                  <div className="absolute top-full right-0 mt-1 border rounded-xl border-gray-100 bg-white p-4 w-72 space-y-3 shadow-xl z-50">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Danh mục
                      </label>
                      <select
                        name="categoryId"
                        value={filters.categoryId}
                        onChange={handleChangeFilters}
                        className="w-full p-2 border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:border-blue-500 transition-colors text-sm text-gray-700"
                      >
                        <option value="">Tất cả danh mục</option>
                        {allCategory.map((cate) => (
                          <option key={cate._id} value={cate._id}>
                            {cate.fullPath?.join(" > ") || cate.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Thương hiệu
                      </label>
                      <select
                        name="brandId"
                        value={filters.brandId}
                        onChange={handleChangeFilters}
                        className="w-full p-2 border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:border-blue-500 transition-colors text-sm text-gray-700"
                      >
                        <option value="">Tất cả thương hiệu</option>
                        {allBrands.map((b) => (
                          <option key={b._id} value={b._id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Trạng thái
                      </label>
                      <select
                        name="isActive"
                        value={String(filters.isActive)}
                        onChange={handleChangeFilters}
                        className="w-full p-2 border border-gray-200 rounded-lg cursor-pointer focus:outline-none focus:border-blue-500 transition-colors text-sm text-gray-700"
                      >
                        <option value="all">Tất cả</option>
                        <option value="true">Hoạt động</option>
                        <option value="false">Ngừng hoạt động</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <button
                        type="button"
                        onClick={handleResetFilter}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        Đặt lại
                      </button>
                      <button
                        type="button"
                        onClick={handleApplyFilter}
                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white transition-colors cursor-pointer"
                      >
                        Áp dụng
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <select
              value={limit}
              onChange={handleChangeLimit}
              className="px-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 cursor-pointer hover:border-blue-500 transition-colors focus:outline-none focus:border-blue-500"
            >
              <option value="10">10 / trang</option>
              <option value="20">20 / trang</option>
              <option value="30">30 / trang</option>
              <option value="50">50 / trang</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <DataTable
          data={products}
          rowKey={(p) => String(p._id)}
          loading={loading.list}
          columns={[
            {
              key: "image",
              header: "Ảnh",
              align: "center",
              width: "80px",
              render: (p) => (
                <img
                  src={p.images?.[0]?.url}
                  alt={p.name}
                  className="h-12 w-12 border border-gray-200 rounded-lg object-cover mx-auto"
                />
              ),
            },
            {
              key: "name",
              header: "Tên sản phẩm",
              align: "left",
              render: (p) => (
                <span className="font-medium text-gray-900">{p.name}</span>
              ),
            },
            {
              key: "category",
              header: "Danh mục",
              align: "left",
              render: (p) => p.categoryId?.name ?? "—",
            },
            {
              key: "brand",
              header: "Thương hiệu",
              align: "left",
              render: (p) => p.brandId?.name ?? "—",
            },
            {
              key: "status",
              header: "Trạng thái",
              align: "center",
              render: (p) =>
                p.isActive ? (
                  <StatusBadge tone="success" label="Hoạt động" />
                ) : (
                  <StatusBadge tone="danger" label="Ngừng" />
                ),
            },
            {
              key: "actions",
              header: "Thao tác",
              align: "right",
              width: "100px",
              render: (p) => (
                <div className="flex items-center gap-1 justify-end">
                  <button
                    type="button"
                    title="Sửa"
                    onClick={() => onUpdate(String(p._id))}
                    className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <TiPencil className="text-lg" />
                  </button>
                  <button
                    type="button"
                    title="Xóa"
                    onClick={() => setConfirmId(String(p._id))}
                    className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <RiDeleteBinLine className="text-lg" />
                  </button>
                </div>
              ),
            },
          ]}
          empty={
            <EmptyState
              icon={<FaBoxOpen />}
              title={
                search || activeFilterCount > 0
                  ? "Không tìm thấy sản phẩm nào"
                  : "Chưa có sản phẩm nào"
              }
              description={
                search || activeFilterCount > 0
                  ? "Thử bỏ bớt bộ lọc hoặc tìm với từ khóa khác"
                  : "Bắt đầu thêm sản phẩm đầu tiên để hiển thị tại đây"
              }
              action={
                !search && activeFilterCount === 0 ? (
                  <button
                    type="button"
                    onClick={onCreate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm text-white cursor-pointer"
                  >
                    + Thêm sản phẩm đầu tiên
                  </button>
                ) : undefined
              }
            />
          }
        />
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center mt-4">
          <Pagination
            pagination={pagination}
            onChange={(newPage) => updateParams({ page: newPage })}
          />
        </div>
      )}

      {confirmId && (
        <ConfirmModal
          message="Bạn có chắc chắn muốn xóa sản phẩm này? Hành động này không thể hoàn tác."
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
};
